import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Card } from "react-bootstrap";
import { IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import { ethers } from "ethers";
import Pagination from "../../Live/components/Pagenation";
import trinityManagerArtifact from "../../../../configs/abis/trinity-manager.json";
import config from "../../../../configs";
import { request } from "../../../../admin/plugins/axios";
import { endpoints } from "../../../../utils/networks";
import { getCommissionHashes } from "../../presaleContractFunctions";
import { copyToClipboard } from "../../../../Helper/Helper";
import { convertToCSV, downloadCSV } from "./csvUtils";
import toast from "react-hot-toast";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const { abi: trinityManagerAbi } = trinityManagerArtifact;

const shorten = (value) =>
  value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "--";

const formatDateTime = (timestamp) => {
  if (!timestamp) return "--";
  try {
    const date = new Date(Number(timestamp) * 1000);
    return date.toLocaleString();
  } catch {
    return "--";
  }
};

const formatBnb = (value) => {
  if (!value) return "0";
  try {
    return Number(ethers.utils.formatEther(value)).toFixed(4);
  } catch {
    return "0";
  }
};

const BSC_TX_BASE_URL = "https://bscscan.com/tx/";
const ITEMS_PER_PAGE = 10;

function CommissionsTable({ provider, signer, refreshKey, isOwner }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hashMap, setHashMap] = useState({});
  const hashRef = useRef({});
  const [addressNames, setAddressNames] = useState({});
  const addressNamesRef = useRef({});
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const managerAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");

  const fetchAddressNames = useCallback(async (addresses = []) => {
    if (!addresses.length) return;
    const normalized = Array.from(
      new Set(
        addresses
          .map((addr) => (typeof addr === "string" ? addr.toLowerCase() : null))
          .filter((addr) => addr && !addressNamesRef.current?.[addr])
      )
    );
    if (!normalized.length) return;
    try {
      const response = await request(
        endpoints.Users.GetNames.method,
        endpoints.Users.GetNames.url,
        { addresses: normalized }
      );
      const users = response?.data?.data?.users || [];
      const mapping = {};
      users.forEach((user) => {
        const lower = user?.wallet_address?.toLowerCase?.();
        if (lower) {
          mapping[lower] = user?.name || user?.username || user?.wallet_address;
        }
      });
      if (Object.keys(mapping).length) {
        addressNamesRef.current = {
          ...addressNamesRef.current,
          ...mapping,
        };
        setAddressNames((prev) => ({ ...prev, ...mapping }));
      }
    } catch (err) {
      console.error("Failed to fetch user names", err);
    }
  }, []);

  useEffect(() => {
    console.log("🟢 CommissionsTable useEffect triggered", {
      provider: !!provider,
      managerAddress,
      page,
      refreshKey,
    });

    if (!provider || !managerAddress) {
      console.log("⚠️ CommissionsTable: Missing provider or managerAddress");
      return;
    }
    if (!signer || !isOwner) {
      setRows([]);
      setTotal(0);
      setError("Connect with an authorized owner wallet to view commissions.");
      return;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        console.log("📤 CommissionsTable: Creating contract...", {
          managerAddress,
        });
        const contract = new ethers.Contract(
          managerAddress,
          trinityManagerAbi,
          signer
        );
        const pageNo = page - 1; // Convert to 0-based page number
        console.log("📤 CommissionsTable: Calling getAllCommissionsRange", {
          pageNo,
          perPage: ITEMS_PER_PAGE,
        });

        const [commissions, totalCount] = await contract.getAllCommissionsRange(
          ethers.BigNumber.from(String(pageNo)),
          ethers.BigNumber.from(String(ITEMS_PER_PAGE))
        );

        console.log("✅ CommissionsTable: Raw response", {
          commissions: commissions?.length || 0,
          totalCount: totalCount?.toString(),
          commissionsArray: commissions,
        });

        const parsed = commissions.map((commission, index) => ({
          id: commission.id?.toString() || `${pageNo * ITEMS_PER_PAGE + index}`,
          commissionId: commission.id?.toString() || "0",
          from: commission.from,
          to: commission.to,
          amount: commission.amount,
          timestamp: commission.timestamp?.toString() || "0",
        }));
        const addressesForNames = [];
        parsed.forEach((item) => {
          if (item.from) addressesForNames.push(item.from);
          if (item.to) addressesForNames.push(item.to);
        });
        fetchAddressNames(addressesForNames);

        console.log("✅ CommissionsTable: Parsed data", {
          parsedCount: parsed.length,
          parsed,
        });

        if (!cancelled) {
          setRows(parsed);
          setTotal(Number(totalCount) || 0);
          console.log("✅ CommissionsTable: State updated", {
            rowsCount: parsed.length,
            total: Number(totalCount) || 0,
          });
        }
      } catch (err) {
        console.error("❌ CommissionsTable: Failed to fetch commissions", err);
        console.error("❌ CommissionsTable: Error details", {
          message: err?.message,
          code: err?.code,
          data: err?.data,
          reason: err?.reason,
        });
        if (!cancelled) {
          setError("Unable to fetch commission transactions.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    provider,
    signer,
    refreshKey,
    page,
    managerAddress,
    isOwner,
    fetchAddressNames,
  ]);

  useEffect(() => {
    const missing = rows
      .map((row) => Number(row.commissionId))
      .filter((id) => id && !hashRef.current[id]);
    if (!missing.length) return;
    let cancelled = false;
    const loadHashes = async () => {
      try {
        const result = await getCommissionHashes(missing);
        if (cancelled) return;
        const updated = { ...hashRef.current };
        if (Array.isArray(result)) {
          result.forEach((item) => {
            const transactionId = Number(item?.transaction_id);
            const hash = item?.transaction_hash;
            if (transactionId && hash) {
              updated[transactionId] = hash;
            }
          });
        } else if (result && typeof result === "object") {
          Object.entries(result).forEach(([key, value]) => {
            const transactionId = Number(key);
            if (transactionId && value) {
              updated[transactionId] = value;
            }
          });
        }
        hashRef.current = updated;
        setHashMap({ ...updated });
      } catch (err) {
        console.error("Failed to load commission hashes", err);
      }
    };
    loadHashes();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handleDownloadCSV = useCallback(async () => {
    if (!provider || !managerAddress || !signer || !isOwner) {
      toast.error("Please connect with an authorized owner wallet.");
      return;
    }

    setDownloadingCSV(true);
    let loadingToast = null;
    try {
      const contract = new ethers.Contract(
        managerAddress,
        trinityManagerAbi,
        signer
      );

      // Get total count
      const [, totalCount] = await contract.getAllCommissionsRange(
        ethers.BigNumber.from("0"),
        ethers.BigNumber.from("1")
      );
      const total = Number(totalCount) || 0;

      if (total === 0) {
        toast("No commissions to export.");
        setDownloadingCSV(false);
        return;
      }

      loadingToast = toast.loading(`Fetching ${total} commissions...`);

      // Fetch all commissions in batches
      const allCommissions = [];
      const batchSize = 100;
      let currentPage = 0;

      while (allCommissions.length < total) {
        const [commissions] = await contract.getAllCommissionsRange(
          ethers.BigNumber.from(String(currentPage)),
          ethers.BigNumber.from(String(batchSize))
        );

        if (!commissions || commissions.length === 0) break;

        const parsed = commissions.map((commission) => ({
          commissionId: commission.id?.toString() || "0",
          from: commission.from,
          to: commission.to,
          amount: commission.amount,
          timestamp: commission.timestamp?.toString() || "0",
        }));

        allCommissions.push(...parsed);
        currentPage += batchSize;

        // Fetch names for this batch
        const addressesForNames = [];
        parsed.forEach((item) => {
          if (item.from) addressesForNames.push(item.from);
          if (item.to) addressesForNames.push(item.to);
        });
        await fetchAddressNames(addressesForNames);
      }

      // Fetch all transaction hashes
      const commissionIds = allCommissions.map((c) => Number(c.commissionId));
      const hashes = await getCommissionHashes(commissionIds);
      const hashMapForCSV = {};
      if (Array.isArray(hashes)) {
        hashes.forEach((item) => {
          const transactionId = Number(item?.transaction_id);
          const hash = item?.transaction_hash;
          if (transactionId && hash) {
            hashMapForCSV[transactionId] = hash;
          }
        });
      } else if (hashes && typeof hashes === "object") {
        Object.entries(hashes).forEach(([key, value]) => {
          const transactionId = Number(key);
          if (transactionId && value) {
            hashMapForCSV[transactionId] = value;
          }
        });
      }

      // Prepare CSV data
      const csvData = allCommissions.map((commission) => {
        const fromLower = commission.from?.toLowerCase?.();
        const toLower = commission.to?.toLowerCase?.();
        const fromName =
          fromLower && addressNamesRef.current[fromLower]
            ? addressNamesRef.current[fromLower]
            : "";
        const toName =
          toLower && addressNamesRef.current[toLower]
            ? addressNamesRef.current[toLower]
            : "";
        const txHash = hashMapForCSV[Number(commission.commissionId)] || "";
        return {
          txHash: txHash,
          date: formatDateTime(commission.timestamp),
          fromAddress: commission.from || "",
          fromName: fromName,
          toAddress: commission.to || "",
          toName: toName,
          amountBNB: formatBnb(commission.amount),
        };
      });

      const headers = [
        { key: "txHash", label: "Tx Hash" },
        { key: "date", label: "Date" },
        { key: "fromAddress", label: "From Address" },
        { key: "fromName", label: "From Name" },
        { key: "toAddress", label: "To Address" },
        { key: "toName", label: "To Name" },
        { key: "amountBNB", label: "Amount (BNB)" },
      ];

      const csvContent = convertToCSV(csvData, headers);
      const filename = `commissions_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      downloadCSV(csvContent, filename);
      toast.dismiss(loadingToast);
      toast.success(
        `Successfully exported ${allCommissions.length} commissions.`
      );
    } catch (err) {
      console.error("Failed to export CSV", err);
      if (loadingToast) toast.dismiss(loadingToast);
      toast.error("Failed to export CSV. Check console for details.");
    } finally {
      setDownloadingCSV(false);
    }
  }, [provider, signer, managerAddress, isOwner, fetchAddressNames]);

  const body = useMemo(() => {
    const colSpan = 5;
    if (!provider || !managerAddress) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Connect to the correct network to view commissions.
          </td>
        </tr>
      );
    }
    if (!signer || !isOwner) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Connect with an authorized owner wallet to view commissions.
          </td>
        </tr>
      );
    }
    if (loading) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            Loading commissions...
          </td>
        </tr>
      );
    }
    if (error) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            {error}
          </td>
        </tr>
      );
    }
    if (!rows.length) {
      return (
        <tr>
          <td className="table-no-data" colSpan={colSpan}>
            No commission transactions found.
          </td>
        </tr>
      );
    }

    return rows.map((row) => {
      const fromLower = row.from?.toLowerCase?.();
      const toLower = row.to?.toLowerCase?.();
      const fromName = fromLower ? addressNames[fromLower] : null;
      const toName = toLower ? addressNames[toLower] : null;
      const txHash = hashMap[Number(row.commissionId)];

      const renderAddress = (address, resolvedName) => (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
          <Tooltip title={address || "--"} placement="top" arrow>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 1.1,
              }}
            >
              {resolvedName && (
                <span
                  style={{
                    fontSize: "1rem",
                    color: "#bfc3d9",
                    marginBottom: 4,
                  }}
                >
                  {resolvedName}
                </span>
              )}
              <span style={{ fontSize: "0.75rem" }}>{shorten(address)}</span>
            </div>
          </Tooltip>
          {address && (
            <IconButton
              className="totalPriceH2"
              size="small"
              onClick={() => copyToClipboard(address)}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </div>
      );

      return (
        <tr key={row.id}>
          <td>
            {txHash ? (
              <Tooltip title={txHash} placement="top" arrow>
                <a
                  href={`${BSC_TX_BASE_URL}${txHash}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hash-link"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  {shorten(txHash)}
                  <LaunchIcon sx={{ fontSize: 16, color: "#fff" }} />
                </a>
              </Tooltip>
            ) : (
              <span>Loading...</span>
            )}
          </td>
          <td>{formatDateTime(row.timestamp)}</td>
          <td>{renderAddress(row.from, fromName)}</td>
          <td>{renderAddress(row.to, toName)}</td>
          <td>{formatBnb(row.amount)} BNB</td>
        </tr>
      );
    });
  }, [
    rows,
    loading,
    error,
    provider,
    managerAddress,
    signer,
    isOwner,
    hashMap,
    addressNames,
  ]);

  return (
    <>
      <Card className="bg-live">
        <Card.Body>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h5 className="mb-0">Commissions</h5>
            <button
              type="button"
              className="summary-copy-btn1"
              onClick={handleDownloadCSV}
              disabled={downloadingCSV || !isOwner || !signer || total === 0}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity:
                  downloadingCSV || !isOwner || !signer || total === 0
                    ? 0.6
                    : 1,
                cursor:
                  downloadingCSV || !isOwner || !signer || total === 0
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <FileDownloadIcon sx={{ fontSize: 18 }} />
              {downloadingCSV ? "Exporting..." : "Download CSV"}
            </button>
          </div>
          <div className="table-responsive-wrapper">
            <table className="purchasing-table">
              <thead>
                <tr>
                  <th>Tx Hash</th>
                  <th>Date</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Amount (BNB)</th>
                </tr>
              </thead>
              <tbody>{body}</tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
      {total > 0 && (
        <Pagination
          totalList={total}
          pages={totalPages}
          currentPage={page}
          setCurrentPage={setPage}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      )}
    </>
  );
}

export default CommissionsTable;
